import {useState} from 'react'
import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from '@headlessui/react'
import {carSideIcon, editPenIcon, infoIcon} from '@img/index.js'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import FlatRateBox from './FlatRateBox.jsx'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import AirportPickupFeeInfo from './PageModals/AirportPickupFeeInfo.jsx'

function formatBreakdownValue(val, isLast) {
    if (isLast) return val < 0 ? `-$${Math.ceil(Math.abs(val / 100))}` : `$${Math.ceil(val / 100)}`
    return val < 0 ? `-$${Math.abs(val / 100).toFixed(2)}` : `$${(val / 100).toFixed(2)}`
}

// NOTE: this card no longer has a "no fare yet" placeholder state. /confirm quotes the fare on
// load (calculate-route → calculate-cost) instead of waiting for a date/time, so the mocked
// vehicle + veiled dummy breakdown that used to stand in for it were removed along with the
// "Add date/time to see the price" copy. While a quote is in flight `isRecalculating` shows a
// loader in the breakdown, and a card with no vehicle simply renders its "Vehicle Detail" header.

// `readOnly` = the view page: no pencil, and the gratuity % renders as plain text
// instead of a dropdown (a disabled Listbox would still look like a control).
export default function CarDetailsCard({trip, driverTip, setDriverTip, onTipChange, isRecalculating, onEditCar, carPolicy = {canEdit: true, blockedReason: null}, gratuityPolicy = {canEdit: true, blockedReason: null}, readOnly = false}) {
    const vehicle = trip?.selected_vehicle
    const breakdownItems = vehicle?.pricing_breakdown || vehicle?.line_by_line_breakdown || []
    // Read at RENDER time, not module load — DRIVER_TIP_OPTIONS is a config-backed getter
    // that returns [] until /config/app resolves; a module-level const captures the empty array.
    const tipOptions = BOOKING_CONSTRAINTS.DRIVER_TIP_OPTIONS

    const [isTipRecalculating, setIsTipRecalculating] = useState(false)
    const [airportFeePopup, setAirportFeePopup] = useState(false)

    return (
        <div className="bg-white p-5 rounded-(--radius-2xl) flex-1 max-md:w-full">
            {/* Selected car header */}
            {vehicle ? (
                <div className="flex items-center gap-3 mb-5">
                    {vehicle.vehicle_image
                        ? <img src={vehicle.vehicle_image} alt="" className="w-12 h-12 object-contain shrink-0"/>
                        : <img src={carSideIcon} alt="Car" className="max-h-9 shrink-0"/>}
                    <span className="text-lg font-semibold">{vehicle.vehicle_name || 'Vehicle'} <span className="text-(--text-secondary) font-normal">{vehicle.vehicle_reference || 'Or Similar'}{(vehicle.vehicles_needed || 1) > 1 ? ` X${vehicle.vehicles_needed}` : ''}</span></span>
                </div>
            ) : (
                <div className="flex items-center gap-3 mb-5">
                    <img src={carSideIcon} alt="Car" className="max-h-9 shrink-0"/>
                    <span className="text-lg font-semibold">Vehicle Detail</span>
                </div>
            )}

            {/* Pricing breakdown */}
            {isRecalculating ? (
                <div className="text-center py-4"><Loader color="#201e20"/></div>
            ) : vehicle ? (
                <div className="max-h-75 overflow-y-auto">
                    {breakdownItems.length > 0 ? (
                        breakdownItems.filter(item => Number(item.value) !== 0).map((item, index, arr) => {
                            const isLast = index === arr.length - 1
                            return (
                                <div key={item.key || index} className="flex justify-between items-center text-sm mb-3.5">
                                    <span className="flex items-center gap-1">
                                        {item.label}
                                        {item.key === 'gratuity' && (
                                            readOnly ? (
                                                <span className="text-sm border border-(--border-color) rounded-full px-3 py-1 ml-1">{driverTip}%</span>
                                            ) : isTipRecalculating ? (
                                                <Loader color="#201e20" height={8} width={1}/>
                                            ) : (
                                                <Listbox value={driverTip} onChange={async (val) => {
                                                    setDriverTip(val)
                                                    setIsTipRecalculating(true)
                                                    if (onTipChange) await onTipChange(val)
                                                    setIsTipRecalculating(false)
                                                }} disabled={isTipRecalculating || !gratuityPolicy.canEdit}>
                                                    <ListboxButton title={!gratuityPolicy.canEdit ? gratuityPolicy.blockedReason || '' : undefined}
                                                                   className="text-sm border border-(--border-color) rounded-full px-3 py-1 ml-1 outline-none cursor-pointer flex items-center gap-1 data-disabled:opacity-50 data-disabled:cursor-not-allowed">
                                                        {driverTip}%
                                                        <svg width="10" height="6" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                    </ListboxButton>
                                                    <ListboxOptions anchor="bottom" className="mt-1 rounded-xl bg-white shadow-lg overflow-hidden z-99999 outline-none">
                                                        {tipOptions.map(v => (
                                                            <ListboxOption key={v} value={String(v)} className="px-4 py-2 text-sm cursor-pointer data-focus:bg-(--bg-grey) data-selected:font-bold">
                                                                {v}%
                                                            </ListboxOption>
                                                        ))}
                                                    </ListboxOptions>
                                                </Listbox>
                                            )
                                        )}
                                        {item.key === 'airport_pickup_fee' && (
                                            <button type="button" onClick={() => setAirportFeePopup(true)}
                                                    className="cursor-pointer bg-transparent border-0 p-0">
                                                <img src={infoIcon} alt="Info" className="w-4 h-4"/>
                                            </button>
                                        )}
                                    </span>
                                    <span className="font-semibold text-lg">
                                        {formatBreakdownValue(item.value, isLast)}
                                    </span>
                                </div>
                            )
                        })
                    ) : (
                        <div className="flex justify-between items-center text-sm">
                            <span>Total</span>
                            <span className="text-lg font-bold">${Math.ceil((vehicle.total_price || 0) / 100)}</span>
                        </div>
                    )}
                    <FlatRateBox/>
                </div>
            ) : (
                <p className="text-sm text-(--text-secondary) mb-4">No vehicle selected</p>
            )}

            {!readOnly && (
            <div className="flex items-end justify-end mt-4">
                <div className="relative shrink-0 group">
                    <button type="button" disabled={!carPolicy.canEdit} onClick={onEditCar}
                            className="cursor-pointer w-10 h-10 shrink-0 rounded-full bg-(--text-primary) flex items-center justify-center p-0 border-0 disabled:opacity-50 disabled:cursor-not-allowed">
                        <img src={editPenIcon} alt="Edit"/>
                    </button>
                    {!carPolicy.canEdit && carPolicy.blockedReason && (
                        <div className="hidden group-hover:block absolute top-full right-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5">
                            <p className="mb-0!">{carPolicy.blockedReason}</p>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* Airport Fee Popup */}
            <Modal isOpen={airportFeePopup} onClose={() => setAirportFeePopup(false)}>
                <ModalHeader title="Airport Pickup Fee" onClose={() => setAirportFeePopup(false)}/>
                <AirportPickupFeeInfo/>
            </Modal>
        </div>
    )
}
